/**
 * SoftAdvisoryStrip — Wave 2 non-blocking intelligence chips.
 *
 * Renders 4 chips (sector breadth, cross-asset, vol confirm, GEX) with a discrete state
 * (GREEN / RED / NEUTRAL / N/A). Every chip has a hover tooltip with the numeric detail.
 * Does NOT gate any decision — chips are pure context for the operator during the
 * 30–60 day outcome-tracking phase, so they stay graphite: text + icon, never a decision colour.
 */
import { Badge, Panel } from "@/sentinel";

const STATE_ICONS = { GREEN: "CircleCheck", RED: "CircleX", NEUTRAL: "CircleMinus", "N/A": "CircleSlash" };

const LABELS = {
  sector_breadth: "Sector breadth",
  cross_asset: "Risk-off cross-asset",
  vol_confirm: "Vol ETF confirm",
  gex: "Dealer GEX",
};

const pct = (v) => `${v >= 0 ? "+" : "−"}${Math.abs(v).toFixed(2)}%`;

function detailFor(key, adv) {
  const s = adv?.state || "N/A";
  if (s === "N/A") return adv?.error || "unavailable";
  if (key === "sector_breadth") {
    const p = Math.round((adv.value || 0) * 100);
    return `${p}% of ${adv.counted}/${adv.total} sectors above VWAP`;
  }
  if (key === "cross_asset") {
    const c = adv.components || {};
    const parts = [];
    if (c.HYG != null) parts.push(`HYG ${pct(c.HYG)}`);
    if (c.TLT != null) parts.push(`TLT ${pct(c.TLT)}`);
    if (c.GLD != null) parts.push(`GLD ${pct(c.GLD)}`);
    if (c.UUP != null) parts.push(`UUP ${pct(c.UUP)}`);
    return `${adv.votes || 0} risk-off votes · ${parts.join(" · ")}`;
  }
  if (key === "vol_confirm") {
    const c = adv.components || {};
    const parts = [];
    if (c.VXX != null) parts.push(`VXX ${pct(c.VXX)}`);
    if (c.UVXY != null) parts.push(`UVXY ${pct(c.UVXY)}`);
    return parts.join(" · ") + (adv.vix_direction ? ` · VIX ${adv.vix_direction}` : "");
  }
  if (key === "gex") {
    const v = adv.value_usd_per_pct || 0;
    return `$${(v / 1e6).toFixed(1)}M per 1% · ${adv.label || ""}`;
  }
  return "";
}

export function SoftAdvisoryStrip({ advisories, dense = false }) {
  if (!advisories || Object.keys(advisories).length === 0) return null;
  const gex = advisories.gex || {};
  return (
    <Panel title="Soft advisories" icon="Info" meta={<span>Non-blocking · 30-day outcome tracked</span>} data-testid="soft-advisory-strip">
      <div className="sn-row" style={{ gap: "var(--space-2)" }}>
        {Object.keys(LABELS).map((key) => {
          const adv = advisories[key] || {};
          const state = adv.state || "N/A";
          const detail = detailFor(key, adv);
          return (
            <div key={key} data-testid={`soft-chip-${key}`} title={detail} className="sn-row"
              style={{ gap: "var(--space-2)", flexWrap: "nowrap", minWidth: 0, maxWidth: "100%", padding: dense ? "2px 8px" : "4px 8px",
                border: "1px solid var(--border-strong)", borderRadius: "var(--radius-sm)", background: "var(--surface-raised)" }}>
              <span className="sn-small sn-muted" style={{ whiteSpace: "nowrap" }}>{LABELS[key]}</span>
              <Badge icon={STATE_ICONS[state] || STATE_ICONS["N/A"]}>{state}</Badge>
              {!dense && (
                <span className="sn-small sn-faint sn-num" style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {detail}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {gex.strike_curve && gex.strike_curve.length > 0 && (
        <div style={{ marginTop: "var(--space-3)" }} data-testid="gex-strike-sparkline">
          <div className="sn-micro" style={{ marginBottom: "var(--space-1)" }}>
            GEX by strike · spot <span className="sn-num">{gex.spot?.toFixed(1) || "—"}</span>
          </div>
          <GexSparkline curve={gex.strike_curve} spot={gex.spot} />
        </div>
      )}
    </Panel>
  );
}

function GexSparkline({ curve, spot }) {
  const W = 240, H = 34, PAD_X = 2;
  if (!curve || curve.length === 0) return null;
  const strikes = curve.map((c) => c.strike);
  const values = curve.map((c) => c.gex_usd_per_pct);
  const kMin = Math.min(...strikes);
  const kMax = Math.max(...strikes);
  const vMax = Math.max(...values.map(Math.abs), 1);
  const barW = Math.max(2, (W - PAD_X * 2) / curve.length - 1);
  const mid = H / 2;
  const spotX = spot ? PAD_X + ((spot - kMin) / (kMax - kMin || 1)) * (W - PAD_X * 2) : null;
  return (
    <svg className="sn-chart" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Dealer gamma exposure by strike; bars above the line are positive, below are negative"
      style={{ width: W, height: H, background: "var(--surface-sunken)", borderRadius: "var(--radius-xs)" }}>
      <line className="zero" x1="0" y1={mid} x2={W} y2={mid} />
      {curve.map((c, i) => {
        const x = PAD_X + i * ((W - PAD_X * 2) / curve.length);
        const h = (Math.abs(c.gex_usd_per_pct) / vMax) * (mid - 1);
        const y = c.gex_usd_per_pct >= 0 ? mid - h : mid;
        const fill = c.gex_usd_per_pct >= 0 ? "var(--viz-1)" : "var(--viz-2)";
        const tip = `${c.strike} · $${(c.gex_usd_per_pct / 1e6).toFixed(1)}M/1%`;
        return (
          <rect key={i} x={x} y={y} width={barW} height={Math.max(0.5, h)} style={{ fill }}>
            <title>{tip}</title>
          </rect>
        );
      })}
      {spotX != null && <line className="xhair" x1={spotX} y1="0" x2={spotX} y2={H} />}
    </svg>
  );
}
